import {
  selectIsLocalAudioEnabled,
  selectLocalPeer,
  selectLocalPeerID,
  selectPeerAudioByID,
  selectPeers,
  useHMSStore,
} from "@100mslive/hms-video-react";
import React, { useEffect, useRef, useState } from "react";
import { ROLES } from "../../common/roles";

// How long the local mic must stay unmuted at ZERO level before we warn.
// Deliberately long: the SDK level is a SPEAKING level, so someone who is
// merely pausing reads identically to a dead mic. Three minutes is still far
// earlier than the 70-minute incident below, while short gaps — reading a
// slide, setting up a screen share — no longer trip it.
const SILENT_AFTER_MS = 3 * 60 * 1000;
// After a manual dismiss, stay quiet for this long before re-warning.
const SNOOZE_MS = 5 * 60 * 1000;

// Only people who are expected to be heard get warned. An attendee who simply
// forgot to mute is not a problem worth interrupting, and warning them anyway
// teaches the whole room to dismiss this on reflex — which is exactly how the
// guard stops working for the case it exists for.
const WARNED_ROLES = [ROLES.HOST, ROLES.PRESENTER];

/**
 * Warns the speaker when their microphone is unmuted but no sound is being
 * captured — the exact failure of the 2026-07-30 webinar: the host's audio
 * track was published and unmuted for 70 minutes, the mute button showed
 * "live", and yet the capture carried pure silence (wrong input device /
 * OS-level dead mic). Nothing in the UI hinted at it; attendees had to
 * unmute themselves to say "we can't hear you".
 *
 * The SDK's audio level for the LOCAL peer comes from the actual captured
 * samples, so a flatline here means the room hears nothing, regardless of
 * what the mute button says. It cannot, however, tell a dead mic apart from
 * a working mic nobody is talking into — hence the long window above, the
 * role scoping, and the advisory (not alarm) treatment.
 */
export const SilentMicWarning = () => {
  const isAudioOn = useHMSStore(selectIsLocalAudioEnabled);
  const localPeerId = useHMSStore(selectLocalPeerID);
  const localPeer = useHMSStore(selectLocalPeer);
  const audioLevel = useHMSStore(selectPeerAudioByID(localPeerId));
  const peers = useHMSStore(selectPeers);

  // Live role rather than the join-time one: a guest promoted to presenter
  // mid-call is precisely the person who is about to start talking.
  const isWarnedRole = WARNED_ROLES.includes(localPeer?.roleName);
  // Alone in the room, nobody can fail to hear you. Bots are excluded so this
  // agrees with the "N in room" count in the participant list, which filters
  // the beam recorder out the same way.
  const hasAudience = peers.some(
    p => p.id !== localPeerId && p.roleName !== ROLES.BOT
  );

  const [warn, setWarn] = useState(false);
  // Timestamp of the last moment we heard ANY signal (or were muted —
  // muted resets the clock so unmuting starts a fresh window).
  const lastSignalAt = useRef(Date.now());
  const snoozedUntil = useRef(0);

  useEffect(() => {
    if (!isAudioOn || (audioLevel || 0) > 0) {
      lastSignalAt.current = Date.now();
      if (warn) setWarn(false);
    }
  }, [isAudioOn, audioLevel, warn]);

  useEffect(() => {
    if (!isWarnedRole || !hasAudience) {
      setWarn(false);
      return undefined;
    }
    // Re-arming — role granted, mic unmuted, or the first listener arriving —
    // starts a fresh window. Otherwise time spent alone in an empty room would
    // count against the speaker the instant someone joins.
    lastSignalAt.current = Date.now();
    const timer = setInterval(() => {
      const silentFor = Date.now() - lastSignalAt.current;
      const snoozed = Date.now() < snoozedUntil.current;
      setWarn(isAudioOn && !snoozed && silentFor >= SILENT_AFTER_MS);
    }, 1000);
    return () => clearInterval(timer);
  }, [isAudioOn, isWarnedRole, hasAudience]);

  if (!warn) {
    return null;
  }

  return (
    <div
      role="status"
      className="fixed left-1/2 flex items-center"
      style={{
        top: 76,
        transform: "translateX(-50%)",
        zIndex: 60,
        gap: 8,
        maxWidth: "min(440px, 92vw)",
        background: "#f59e0b",
        color: "#1f2937",
        padding: "8px 14px",
        borderRadius: 999,
        boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
        fontSize: 13,
        lineHeight: 1.4,
      }}
    >
      <span style={{ fontSize: 14 }} aria-hidden>
        🎤
      </span>
      <span>
        <strong>Mic may not be working</strong> — no sound detected. Check your
        input device in Settings.
      </span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => {
          snoozedUntil.current = Date.now() + SNOOZE_MS;
          setWarn(false);
        }}
        style={{
          background: "transparent",
          border: 0,
          color: "#1f2937",
          cursor: "pointer",
          fontSize: 14,
          fontWeight: 700,
          opacity: 0.7,
          padding: "0 2px",
        }}
      >
        ✕
      </button>
    </div>
  );
};

/**
 * Tiny live input-level meter rendered beside the mic toggle — three bars
 * that light up with the local capture level, so a speaker can SEE their
 * mic working (or not) at a glance without waiting for the warning.
 *
 * Unlike the warning above this stays available to every role: it is passive,
 * has no false-positive cost, and lets an attendee confirm their mic before
 * they speak.
 */
export const LocalMicLevel = () => {
  const isAudioOn = useHMSStore(selectIsLocalAudioEnabled);
  const localPeerId = useHMSStore(selectLocalPeerID);
  const audioLevel = useHMSStore(selectPeerAudioByID(localPeerId)) || 0;

  if (!isAudioOn) {
    return null;
  }

  // SDK levels run 0-100. Three thresholds keep it readable at 12px.
  const bars = [audioLevel > 5, audioLevel > 30, audioLevel > 60];

  return (
    <span
      className="inline-flex items-end"
      aria-label="Microphone input level"
      title="Microphone input level"
      style={{ gap: 2, height: 14, marginLeft: 2, marginRight: 6 }}
    >
      {bars.map((on, i) => (
        <span
          key={i}
          style={{
            width: 3,
            height: 5 + i * 4,
            borderRadius: 1,
            background: on ? "#22c55e" : "rgba(255,255,255,0.25)",
            transition: "background 120ms",
          }}
        />
      ))}
    </span>
  );
};
